import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Autocomplete, Chip, TextField } from '@mui/material'

import { InternalTag } from '../../../../types/InternalTag'

type FtueChapterTagsFieldProps = {
  value: string[]
  tags: InternalTag[]
  onChange: (tagIds: string[]) => void
  label: string
  disabled?: boolean
}

type TagOption = { id: string; name: string; unknown?: boolean }

/** The chapter's FTUE tags, in the order they were picked; customers see them under the chapter's time. */
export const FtueChapterTagsField: FC<FtueChapterTagsFieldProps> = ({ value, tags, onChange, label, disabled }) => {
  const { t } = useTranslation()
  const options = useMemo<TagOption[]>(
    () =>
      tags
        .filter((tag) => tag.active !== false)
        .map(({ id, name }) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tags]
  )
  const byId = useMemo(() => new Map(tags.map((tag) => [tag.id, tag])), [tags])
  const selected = value.map((id): TagOption => {
    const tag = byId.get(id)
    return tag ? { id, name: tag.name } : { id, name: t('internal-analysts:ftue_videos_unknown_tag', { id }), unknown: true }
  })

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={selected}
      disabled={disabled}
      disableCloseOnSelect
      filterSelectedOptions
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, other) => option.id === other.id}
      onChange={(_event, picked) => onChange(picked.map(({ id }) => id))}
      renderTags={(picked, getTagProps) =>
        picked.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.name} color={option.unknown ? 'warning' : 'default'} />
        ))
      }
      renderInput={(params) => (
        <TextField {...params} label={t('internal-analysts:ftue_videos_chapter_tags')} inputProps={{ ...params.inputProps, 'aria-label': label }} />
      )}
    />
  )
}
